/**
 * Small inline row for system notices: compact boundaries, API
 * retries, and the init frame. Not a banner, just a quiet marker.
 */

import { RefreshCw, Scissors, Wifi } from 'lucide-react';
import type { SystemNotice } from '@/lib/agent/reducer';
import { cn } from '@/lib/utils/cn';

export interface SystemNoticeLineProps {
  notice: SystemNotice;
  className?: string;
}

export function SystemNoticeLine({ notice, className }: SystemNoticeLineProps) {
  return (
    <div
      className={cn(
        'my-2 flex w-fit select-text items-center gap-1.5 rounded-md px-1 py-0.5 text-[10.5px] text-gray-400 dark:text-gray-500',
        className
      )}
    >
      <Icon type={notice.type} className="select-none" />
      <span className="uppercase tracking-wider">{label(notice)}</span>
    </div>
  );
}

function Icon({ type, className }: { type: SystemNotice['type']; className?: string }) {
  switch (type) {
    case 'compact_boundary':
      return <Scissors className={cn('size-3', className)} />;
    case 'api_retry':
      return <RefreshCw className={cn('size-3', className)} />;
    case 'init':
      return <Wifi className={cn('size-3', className)} />;
  }
}

function label(notice: SystemNotice): string {
  switch (notice.type) {
    case 'compact_boundary': {
      const { trigger, pre, post } = compactNoticeData(notice.data);
      const triggerSuffix = trigger ? ` (${trigger})` : '';
      if (pre !== null && post !== null) {
        return `context compacted: ${fmt(pre)} → ${fmt(post)} tokens${triggerSuffix}`;
      }
      if (pre !== null) {
        return `context compacted at ${fmt(pre)} tokens${triggerSuffix}`;
      }
      return `context compacted${triggerSuffix}`;
    }
    case 'api_retry': {
      const attempt = notice.data.attempt as number | undefined;
      const reason = notice.data.reason as string | undefined;
      return `retrying api call${attempt ? ` · attempt ${attempt}` : ''}${reason ? ` · ${reason}` : ''}`;
    }
    case 'init':
      return 'session initialized';
  }
}

interface CompactNoticeData {
  trigger: string | null;
  pre: number | null;
  post: number | null;
}

function compactNoticeData(data: Record<string, unknown>): CompactNoticeData {
  const compactMetadata = compactMetadataObject(data);
  const trigger = stringField(data, compactMetadata, 'trigger');
  const pre = numericField(data, compactMetadata, ['pre_tokens', 'preTokens']);
  const post = numericField(data, compactMetadata, ['post_tokens', 'postTokens']);
  return { trigger, pre, post };
}

function compactMetadataObject(data: Record<string, unknown>): Record<string, unknown> | null {
  const metadata = data.compactMetadata;
  if (metadata !== null && typeof metadata === 'object' && !Array.isArray(metadata)) {
    return metadata as Record<string, unknown>;
  }
  return null;
}

function stringField(
  data: Record<string, unknown>,
  metadata: Record<string, unknown> | null,
  key: string
): string | null {
  const topLevel = data[key];
  if (typeof topLevel === 'string') return topLevel;
  const nested = metadata?.[key];
  return typeof nested === 'string' ? nested : null;
}

function numericField(
  data: Record<string, unknown>,
  metadata: Record<string, unknown> | null,
  keys: readonly string[]
): number | null {
  for (const source of [data, metadata]) {
    if (!source) continue;
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    }
  }
  return null;
}

function fmt(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}
